import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { isArchivedAssignment } from './assignmentHistory';

/**
 * Defaults for the location fields of the visit form.
 *
 * Users were re-entering the same hospital and city on every single visit, even
 * though the project already records which center they work at. These helpers
 * resolve a sensible default so the form can fill itself in.
 *
 * `Visits.hospital` holds a `Centers` document id, `Visits.city` holds the
 * center's `İl` value as plain text.
 */

/**
 * The center ids an employee is assigned to on a project. `Projects.employees`
 * holds one entry per employee *and* center, so an employee working at two
 * centers of the same project appears twice.
 *
 * Centers the employee has left (`unassignedAt`, see utils/assignmentHistory)
 * are deliberately still returned: their past visits reference those centers and
 * must keep resolving to a name instead of falling into the "other hospitals"
 * bucket. Pass `{ activeOnly: true }` when offering choices for *new* work.
 */
export const getEmployeeProjectCenterIds = (project, employeeId, { activeOnly = false } = {}) => {
  if (!project?.employees || !employeeId) return [];

  const entries = Array.isArray(project.employees)
    ? project.employees
    : Object.values(project.employees);

  return Array.from(
    new Set(
      entries
        .filter((entry) => entry?.id === employeeId)
        .filter((entry) => !activeOnly || !isArchivedAssignment(entry))
        .map((entry) => entry?.centerId)
        .filter(Boolean)
    )
  );
};

/** Sortable timestamp for a visit, newest last. */
const getVisitTime = (visit) => {
  const day = visit.date || visit.firstVisitDate;
  if (day) {
    const parsed = new Date(day);
    if (!isNaN(parsed.getTime())) return parsed.getTime();
  }

  const created = visit.createdAt;
  if (created?.toDate) return created.toDate().getTime();
  if (created) {
    const parsed = new Date(created);
    if (!isNaN(parsed.getTime())) return parsed.getTime();
  }

  return 0;
};

/**
 * Hospital and city of the employee's most recent other visit on the project,
 * or null when they have none yet.
 */
export const fetchLastVisitLocation = async (
  projectId,
  employeeId,
  excludeVisitId
) => {
  if (!projectId || !employeeId) return null;

  const snapshot = await getDocs(
    query(
      collection(db, 'Visits'),
      where('projectId', '==', projectId),
      where('employeeId', '==', employeeId)
    )
  );

  const lastVisit = snapshot.docs
    .map((visitDoc) => ({ id: visitDoc.id, ...visitDoc.data() }))
    .filter((candidate) => candidate.id !== excludeVisitId && candidate.hospital)
    .sort((a, b) => getVisitTime(b) - getVisitTime(a))[0];

  return lastVisit
    ? { hospital: lastVisit.hospital, city: lastVisit.city || '' }
    : null;
};
